import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { sortLocalized, type LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import type { Country } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  onEdit: (row: Country) => void
  onToggle: (row: Country, active: boolean) => void
  onDelete: (row: Country) => void
}

/**
 * Ülke tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). TR/EN ad + durum Badge + satır-içi InputSwitch
 * (Aktif/Pasif) + satır aksiyonları (düzenle/sil). REF_COUNTRY'de SORT_INDEX olmadığından (Adım 0) elle sıra
 * kolonu yoktur; liste ada göre sıralı + sayfalıdır. TR/EN ad kolonları **sortable** — tüm kayıtlar istemcide
 * yüklü olduğundan sıralama istemci tarafındadır (removableSort). Korumalı sistem kaydı yoktur.
 */
export function countryColumns({
  t,
  localized,
  busyId,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="name"
      header={t('location.countries.columns.name')}
      // Sıralama GÖRÜNEN değere göre; `field` verilseydi İngilizce arayüzde TR adına göre sıralanır.
      sortable
      sortFunction={(e) =>
        sortLocalized(e.data as Country[], localized, (r) => [r.nameTr, r.nameEn], e.order)
      }
      body={(row: Country) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('location.countries.columns.status')}
      style={{ width: '8rem' }}
      body={(row: Country) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('location.countries.status.active')
              : t('location.countries.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('location.countries.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: Country) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('location.countries.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('location.countries.actions.edit')}
              tooltip={t('location.countries.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('location.countries.actions.delete')}
              tooltip={t('location.countries.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
